import {
  toWSDateString,
  WSButton,
  WSFlexBox,
  WSTable,
  WSTableCell,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  InvoiceStatus,
  IPayableSchema,
  IPayerTaxFormResponse
} from "@wingspanhq/payments/dist/interfaces";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import { flatten } from "lodash";
import times from "lodash/times";
import {
  Route,
  useHistory,
  useLocation,
  useRouteMatch
} from "react-router-dom";
import { PayableDetails } from "../../../../../Invoices/screens/payables/PayableDetails";
import { WSQueries } from "../../../../../query/WSQuery";
import { useUserId } from "../../../../../query/hooks/helpers";
import { useQueryPayables } from "../../../../../query/payments/queries";
import {
  PayablesFilter,
  paymentsService
} from "../../../../../services/payments";
import { ButtonDownloadCsv } from "../../../../../shared/components/ButtonDownloadCsv";
import { concurrentActions } from "../../../../../utils/serviceHelper";
import { selectorTaxFormRecipientName } from "../../../selectors/selectorTaxFormRecipientName";
import { cleanOptionalFormValues } from "../../../../../shared/utils/cleanOptionalFormValues";

type Props = {
  taxForm: IPayerTaxFormResponse;
};

export const Payables: React.FC<Props> = ({ taxForm }) => {
  const userId = useUserId();

  const filter: PayablesFilter = cleanOptionalFormValues({
    memberId: taxForm.memberId,
    status: InvoiceStatus.Paid,
    "lineItems.labels.taxForm1099": taxForm.year,
    "events.paidAt": {
      ">=": taxForm.startDate,
      "<=": taxForm.endDate
    }
  });

  const queryPayables = useQueryPayables(
    {
      filter,
      page: {
        size: 10
      }
    },
    userId !== taxForm.clientId ? taxForm.clientId : undefined
  );
  const payeeName = selectorTaxFormRecipientName(taxForm);
  const history = useHistory();
  const location = useLocation();
  const match = useRouteMatch();

  return (
    <>
      <Route
        path={match.path + "/payments/:payableId"}
        component={PayableDetails}
      />

      <WSFlexBox.CenterY wrap="nowrap" justify="space-between">
        <WSText weight="medium">Payables</WSText>

        {queryPayables.data && queryPayables.data.summary.listSize > 0 && (
          <ButtonDownloadCsv
            getData={async () => {
              const pageSize = 100;
              const allCount = queryPayables.data?.summary.listSize || 0;
              const pagesCount = Math.ceil(allCount / pageSize);

              const actions = times(pagesCount).map(
                (_, i) => () =>
                  paymentsService.payable.list(
                    {
                      filter,
                      page: {
                        size: pageSize,
                        number: i + 1
                      }
                    },
                    userId !== taxForm.clientId ? taxForm.clientId : undefined
                  )
              );

              const allPages = await concurrentActions(actions, {
                concurrentLimit: 5
              });

              return flatten(allPages.map(page => page.data)).map(payable => ({
                "Invoice Number": payable.invoiceNumber,
                "Non-employee compensation": payable.amount,
                "Paid Date": payable.events.paidAt
              }));
            }}
            fileName={`${payeeName} Tax Form Payables`}
            customTrigger={<WSButton.Link>Download CSV</WSButton.Link>}
          />
        )}
      </WSFlexBox.CenterY>

      <WSQueries queries={{ queryPayables }}>
        {({ queryPayablesData: { data: payables } }) => {
          return (
            <>
              <WSTable<IPayableSchema>
                tableData={payables.map(payable => ({
                  id: payable.payableId,
                  data: payable
                }))}
                onRowClick={
                  userId === taxForm.clientId
                    ? ({ data: payable }) => {
                        history.push(
                          location.pathname + "/payments/" + payable.payableId,
                          {
                            backPath: location.pathname
                          }
                        );
                      }
                    : undefined
                }
                columns={[
                  {
                    config: {
                      gridTemplateSizeMax: "1fr",
                      header: {
                        text: `Invoices paid in ${taxForm.year}`
                      }
                    },
                    renderFunction: ({ data: payable }) => (
                      <WSTableCell
                        avatar={{
                          type: "icon",
                          icon: "check-double",
                          color: "green500",
                          colorBackground: "green50"
                        }}
                        text={payable.invoiceNumber}
                      />
                    )
                  },
                  {
                    config: {
                      gridTemplateSizeMax: "1fr",
                      justify: "start",
                      header: {
                        text: "Non-employee compensation"
                      }
                    },
                    renderFunction: ({ data: payable }) => (
                      <WSTableCell
                        text={formatMoney(payable.amount, payable.currency)}
                        secondaryText={`Paid on ${toWSDateString(
                          payable.events.paidAt,
                          "monthDate"
                        )}`}
                      />
                    )
                  }
                ]}
              />
              {payables.length === 0 && (
                <WSText.ParagraphSm align="center" mt="M">
                  No invoices found
                </WSText.ParagraphSm>
              )}
              {queryPayables.hasNextPage && (
                <WSButton.Link
                  mt="M"
                  fullWidth
                  onClick={() => {
                    queryPayables.fetchNextPage();
                  }}
                  loading={!!queryPayables.isFetchingNextPage}
                >
                  Load more
                </WSButton.Link>
              )}
            </>
          );
        }}
      </WSQueries>
    </>
  );
};
